import React from "react";
import { Badge } from "@/components/ui/badge";

interface MovieTagsProps {
  tags: string[];
  className?: string;
  maxTags?: number;
}

const MovieTags: React.FC<MovieTagsProps> = ({ 
  tags, 
  className = "",
  maxTags = 0 // Default to 0 (unlimited)
}) => {
  // Ensure tags is always an array
  const validTags = Array.isArray(tags) ? tags : [];
  
  // Don't render anything if no tags
  if (!validTags.length) return null;
  
  // Format tag to sentence case (first letter uppercase, rest lowercase)
  const formatTag = (tag: string) => {
    if (typeof tag !== 'string') return '';
    return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
  };

  // Helper function to prioritize important tags
  const prioritizeTags = (allTags: string[], limit: number): string[] => {
    if (!limit || limit >= allTags.length) return allTags;
    
    // Priority genres that should be shown first if present
    const priorityGenres = [
      "action", "drama", "comedy", "thriller", "horror", 
      "sci-fi", "romance", "fantasy", "adventure", "animation"
    ];
    
    // Sort tags by priority
    const sortedTags = [...allTags].sort((a, b) => {
      const aLower = typeof a === 'string' ? a.toLowerCase() : '';
      const bLower = typeof b === 'string' ? b.toLowerCase() : '';
      
      const aIndex = priorityGenres.indexOf(aLower);
      const bIndex = priorityGenres.indexOf(bLower);
      
      // If both are priority genres, sort by priority order
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      
      // If only a is a priority genre, it comes first
      if (aIndex !== -1) return -1;
      
      // If only b is a priority genre, it comes first
      if (bIndex !== -1) return 1;
      
      // Otherwise keep original order
      return 0;
    });
    
    return sortedTags.slice(0, limit);
  };

  // Determine tags to display based on maxTags
  const tagsToShow = maxTags > 0 ? prioritizeTags(validTags, maxTags) : validTags;
  
  // Use darker background for grid view to match search results
  const tagColorClass = "bg-mono-outerSpace/90 text-mono-antiflashWhite border-transparent";

  return (
    <div className={`flex flex-wrap gap-1 mt-1 ${className}`}>
      {tagsToShow.map((tag, index) => (
        <Badge 
          key={index}
          variant="outline" 
          className={`${tagColorClass} text-[0.7rem] px-2 py-0.5 font-medium`}
        >
          {formatTag(tag)}
        </Badge>
      ))}
    </div>
  );
};

export default MovieTags;
